import {useEffect, useRef} from 'react';

interface ScrollEvent {
  type: string;
  frameIndex: number;
  isRemote?: boolean;
}

interface MessageEvent {
  data: ScrollEvent;
}

class ScrollThrottler {
  private lastSent = 0;
  private pendingFrame: number | null = null;
  private readonly throttleMs: number;

  constructor(throttleMs = 33) {
    // ~30fps
    this.throttleMs = throttleMs;
  }

  shouldSend(frameIndex: number): boolean {
    const now = Date.now();
    this.pendingFrame = frameIndex;

    if (now - this.lastSent >= this.throttleMs) {
      this.lastSent = now;
      return true;
    }
    return false;
  }

  getPendingFrame(): number | null {
    return this.pendingFrame;
  }
}

export class ScrollHandler {
  private throttler = new ScrollThrottler();
  private pendingTimeout: NodeJS.Timeout | null = null;
  private connection: WebSocket;
  private tourId: string;
  private isActive = true;

  constructor(connection: WebSocket, tourId: string) {
    this.connection = connection;
    this.tourId = tourId;
  }

  private sendScrollMessage(frameIndex: number): void {
    if (this.connection.readyState !== WebSocket.OPEN || !this.isActive) {
      return;
    }

    try {
      this.connection.send(
        JSON.stringify({
          type: 'truvu_scrolling',
          tourId: this.tourId,
          data: {frameIndex},
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error sending scroll message:', error);
    }
  }

  private handlePendingMessage(): void {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
    }

    this.pendingTimeout = setTimeout(() => {
      const pendingFrame = this.throttler.getPendingFrame();
      if (pendingFrame !== null && this.isActive) {
        this.sendScrollMessage(pendingFrame);
      }
      this.pendingTimeout = null;
    }, 100);
  }

  handleScroll = (event: MessageEvent): void => {
    if (
      event.data?.type !== 'truvu_scrolling' ||
      event.data?.isRemote ||
      !this.isActive
    ) {
      return;
    }

    const frameIndex = event.data.frameIndex;

    if (this.throttler.shouldSend(frameIndex)) {
      this.sendScrollMessage(frameIndex);
    } else {
      this.handlePendingMessage();
    }
  };

  pause(): void {
    this.isActive = false;
  }

  resume(): void {
    this.isActive = true;
  }

  cleanup(): void {
    this.isActive = false;

    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }
}

export const useScrollHandler = (
  signalingConnection: WebSocket | null,
  tourId: string
) => {
  const handlerRef = useRef<ScrollHandler | null>(null);

  useEffect(() => {
    if (!signalingConnection) {
      if (handlerRef.current) {
        handlerRef.current.cleanup();
        handlerRef.current = null;
      }
      return;
    }

    handlerRef.current = new ScrollHandler(signalingConnection, tourId);
    const handler = handlerRef.current.handleScroll;

    window.addEventListener('message', handler);

    return () => {
      window.removeEventListener('message', handler);
      if (handlerRef.current) {
        handlerRef.current.cleanup();
        handlerRef.current = null;
      }
    };
  }, [signalingConnection, tourId]);

  return {
    pauseScrollSync: () => handlerRef.current?.pause(),
    resumeScrollSync: () => handlerRef.current?.resume(),
  };
};
